import {
  InstallationModule,
  FormattedDocument,
  FormattedDocumentItem,
  PanelOrientation,
  Material,
  SolarPanel,
  SerialNumber,
  CellItem,
  GridItem,
  Inverter,
} from '@/types';
import store from '@/store/store';

function createGrid(grid: CellItem[], rows: number, columns: number): GridItem[] {
  const size = rows * columns;
  let col = 0,
    row = 0;
  let gridIndex = 1;

  const source: CellItem[] = grid;

  return new Array(size).fill(null).map((d, index) => {
    col = index % columns;
    row = Math.floor(index / columns);
    let value = source.some(item => item.col === col && item.row === row);

    return {
      col,
      row,
      index: value ? gridIndex++ : gridIndex,
      value,
    };
  });
}

function getPanelType(id: number) {
  const panel: SolarPanel = store.getters['materials/panels/findItem'](id);

  return (panel && `${panel.brand} - ${panel.model} (${panel.output} Wp)`) || 'Niet ingevuld';
}

function getInverterType(id: number) {
  const inverter: Material = store.getters['materials/inverters/findItem'](id);

  return (inverter && `${inverter.brand} - ${inverter.model}`) || 'Niet ingevuld';
}

function getInverterConfigurationName(id: number) {
  const config: Material = store.getters['materials/inverterConfigurations/findItem'](id);
  return config.name || 'Niet ingevuld';
}

function getGrid(grid?: CellItem[], rows?: number, columns?: number) {
  if (!grid || !grid.length || !rows || !columns) {
    return [];
  }

  const value = createGrid(grid, rows, columns);

  let index = 0;
  let fillIndex = 0;

  return new Array(rows).fill(null).map(() =>
    new Array(columns)
      .fill(null)
      .map(() => {
        index += 1;

        const filled = value[index - 1];

        if (filled?.value) fillIndex++;

        return filled?.value ? fillIndex : '-';
      })
      .join(' '),
  );
}

function getOrientation(orientation?: PanelOrientation) {
  switch (orientation) {
    case 'landscape':
      return 'Landscape';
    case 'portrait':
      return 'Portrait';
    default:
      return 'onbekend';
  }
}

function getSerialNumbers(items: SerialNumber[] = []): FormattedDocumentItem[] {
  return items.map((item, index) => ({
    type: 'text',
    title: `Serienummer #${index + 1}`,
    value: item.serialNumber ?? 'Niet ingevuld',
  }));
}

function getInverterConfigurations(items: Inverter[] = []): FormattedDocumentItem[] {
  return items.map(item => ({
    type: 'text',
    title: `model: ${item.model}`,
    value: String(item.amount),
  }));
}

export default (mod: InstallationModule): FormattedDocument => [
  {
    title: 'Panelen',
    items: [
      {
        type: 'image',
        title: 'Legplan',
        value: mod.installationPlan ? mod.installationPlan.layoutPlanUrl : 'unknown',
      },
      {
        type: 'text',
        title: 'Type paneel',
        value: getPanelType(mod.data!.panelId!),
      },
      {
        type: 'text',
        title: 'Positie',
        value: mod.data!.locationPanels || 'Niet ingevuld',
      },
      {
        type: 'text',
        title: 'Orientatie',
        value: getOrientation(mod.data!.panelOrientation),
      },
      {
        type: 'text',
        title: 'Aantal panelen',
        value: `${mod.data!.numberOfPanels}`,
      },
      ...getGrid(mod.data!.grid, mod.data!.rows, mod.data!.panelsPerRow).map(
        (row, index) =>
          ({
            type: 'text',
            title: `Grid (rij ${index + 1})`,
            value: row,
          } as FormattedDocumentItem),
      ),
    ],
  },
  {
    title: 'Omvormers',
    items: [
      {
        type: 'text',
        title: 'Omvormer configuratie',
        value: getInverterConfigurationName(mod.data!.inverterConfigurationId!),
      },
      ...getInverterConfigurations(mod.data!.inverterConfiguration),
      ...getSerialNumbers(mod.data!.inverterSerialNumbers),
    ],
  },
];
